// src/components/RangeContainer/RangeContainer.jsx
'use client';

import React, { useMemo, useEffect } from 'react';
import './range-container.css';

const CARD_VALUES = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];

const SUIT_COLORS = {
  hearts: '#E57373',    
  clubs: '#4CAF50',     
  diamonds: '#497CAB',  
  spades: '#424242'     
};

const SUIT_MAP = {
  'h': 'hearts',
  'd': 'diamonds',
  'c': 'clubs',
  's': 'spades'
};

const Card = ({ suit, value, isBlank = false }) => (
  <div className={`board-card small-card ${isBlank ? 'blank' : ''}`}>
    {!isBlank && (
      <>
        <img 
          src={`/svgs/${SUIT_MAP[suit]}.svg`}
          alt={SUIT_MAP[suit]} 
          className="card-suit"
        />
        <span 
          className="card-value"
          style={{ color: SUIT_COLORS[SUIT_MAP[suit]] }}
        >
          {value}
        </span>
      </>
    )}
  </div>
);

const HandStack = ({ firstCard, secondCard, exactHand, index, frequency = 100, isExactHand = false }) => {
  const isPair = firstCard === secondCard;
  
  // Check if the exact hand exists and has valid card values
  const hasValidExactHand = exactHand?.card1 && exactHand?.card2 && 
                          exactHand.card1[1] && exactHand.card2[1];
                          
  // Determine if suited based on the exact hand
  const suited = !isPair && hasValidExactHand && 
                 exactHand.card1[1] === exactHand.card2[1];
  
  console.log(`HandStack for ${firstCard}${secondCard}:`, { 
    isPair, 
    suited, 
    frequency,
    exactHand: exactHand ? `${exactHand.card1 || '?'}${exactHand.card2 || '?'}` : 'undefined'
  });
  
  let suit1, suit2;
  let blankCount;
  
  if (isPair) {
    if (isExactHand) {
      // For exact pair match at 50% frequency
      suit1 = 'h';
      suit2 = 'd';
      blankCount = 1; // Only 1 blank for exact pair at 50%
    } else {
      // For other pairs
      suit1 = 'c';
      suit2 = 's';
      blankCount = frequency === 50 ? 2 : 5; // 2 blanks for 50%, 5 for 100%
    }
  } else if (suited) {
    // Suited hand logic
    if (isExactHand && exactHand.card1 && exactHand.card1[1]) {
      suit1 = exactHand.card1[1] === 'h' ? 'd' : 'h';
      suit2 = suit1;
      blankCount = frequency === 100 ? 2 : 0;
    } else {
      suit1 = exactHand && exactHand.card1 && exactHand.card1[1] ? exactHand.card1[1] : 'h';
      suit2 = suit1;
      blankCount = frequency === 100 ? 3 : 1;
    }
  } else {
    // Offsuit hand logic - FIXED to ensure different suits
    if (isExactHand && hasValidExactHand) {
      // This entry is the same rank-pair as the hero's own hand, which is
      // already shown in the answer circle above. Use a different suit
      // combination here so this stack doesn't visually repeat that exact
      // combo, and show one fewer card since that combo is already
      // accounted for above.
      const heroSuits = [exactHand.card1[1], exactHand.card2[1]];
      const remainingSuits = ['h', 'd', 'c', 's'].filter(s => !heroSuits.includes(s));
      suit1 = remainingSuits[0] || (heroSuits[0] === 'c' ? 's' : 'c');
      suit2 = remainingSuits[1] || (heroSuits[1] === 's' ? 'c' : 's');
      blankCount = 4; // One less than the standard 5 - this combo's slot is already shown above
    } else {
      // For related hands, use standard offsuit combination
      suit1 = 'h';
      suit2 = 'd';
      blankCount = 5; // Always 5 blanks for offsuit hands as per original code
    }
  }

  console.log(`Determined suits for ${firstCard}${secondCard}:`, { suit1, suit2, blankCount });

  const blankCards = Array(blankCount || 0).fill(null);

  return (
    <div className="hand-group">
      <div className="hand-stack">
        {blankCards.map((_, idx) => (
          <div key={`blank-${idx}`} className="card-container">
            <Card isBlank={true} />
            <Card isBlank={true} />
          </div>
        ))}
        <div className="card-container">
          <Card suit={suit1} value={firstCard} />
          <Card suit={suit2} value={secondCard} />
        </div>
      </div>
    </div>
  );
};

// Enhanced analyzeRange function with better error handling
const analyzeRange = (selectedHand, range) => {
  console.log('Starting Range Analysis:', { 
    selectedHand: selectedHand ? 
      `${selectedHand.card1 || '?'}${selectedHand.card2 || '?'}` : 'undefined',
    rangeSize: range?.length || 0
  });
  
  if (!selectedHand || !selectedHand.card1 || !selectedHand.card2 || !range || !range.length) {
    console.warn('Invalid input for range analysis');
    return [];
  }
  
  const firstCard = selectedHand.card1[0];
  const secondCard = selectedHand.card2[0];
  const isPair = firstCard === secondCard;
  const suited = !isPair && selectedHand.card1[1] === selectedHand.card2[1];
  
  console.log('Hand type:', { 
    firstCard, 
    secondCard, 
    isPair, 
    suited, 
    suits: `${selectedHand.card1[1]}${selectedHand.card2[1]}`
  });
  
  const currentHandInRange = range.find(h => 
    h.card1 && h.card2 &&
    h.card1[0] === firstCard && 
    h.card2[0] === secondCard && 
    (isPair || (h.card1[1] === h.card2[1]) === suited)
  );

  console.log('Current hand in range:', currentHandInRange);

  // Handle pairs
  if (isPair) {
    console.log('Processing pair:', firstCard + firstCard);
    
    // Generate all possible pair combinations
    const possiblePairs = CARD_VALUES
      .filter(value => value !== firstCard)
      .map(value => {
        const combo = {
          card1: value + 'c',
          card2: value + 's',
          showBlanks: currentHandInRange?.frequency === 50 ? 2 : 5,
          frequency: 0,
          isExactHand: false,
          value: value
        };
        
        const pairInRange = range.find(h => 
          h.card1 && h.card2 &&
          h.card1[0] === value && 
          h.card2[0] === value
        );
        
        return {
          ...combo,
          inRange: !!pairInRange,
          frequency: pairInRange?.frequency || 0
        };
      });

    let matchingPairs;
    if (currentHandInRange) {
      if (currentHandInRange.frequency === 100) {
        // For 100% frequency, only show other pairs
        matchingPairs = possiblePairs.filter(pair => 
          pair.frequency === 100
        );
      } else if (currentHandInRange.frequency === 50) {
        // For 50% frequency, show other pairs plus our pair
        const otherPairs = possiblePairs.filter(pair => pair.frequency === 50);
        const currentPair = {
          card1: firstCard + 'h',
          card2: firstCard + 'd',
          frequency: 50,
          showBlanks: 1,
          isExactHand: true,
          value: firstCard
        };
        matchingPairs = [currentPair, ...otherPairs];
      }
    } else {
      matchingPairs = possiblePairs.filter(pair => 
        pair.frequency === 0
      );
    }

    // Sort pairs by value using CARD_VALUES order — highest to lowest (AA, KK,
    // QQ, ...), matching the ordering already used for suited/offsuit hands
    // below. Pairs previously sorted ascending (22, 33, ...), which was
    // inconsistent with every other "Other hands" list in the app.
    matchingPairs.sort((a, b) => {
      const aIdx = CARD_VALUES.indexOf(a.value || a.card1[0]);
      const bIdx = CARD_VALUES.indexOf(b.value || b.card1[0]);
      return bIdx - aIdx; // Descending order (AA, KK, QQ, ...)
    });

    return matchingPairs || [];
  }

  // Handle high card hands (suited or offsuit)
  if (!isPair) {
    console.log('Processing high card hand:', firstCard + secondCard + (suited ? 's' : 'o'));
    
    const possibleCombos = CARD_VALUES
      .filter(value => {
        const valueIdx = CARD_VALUES.indexOf(value);
        return valueIdx < CARD_VALUES.indexOf(firstCard);
      })
      .map(value => {
        // Create the hand combination with proper suits
        let card1Suit, card2Suit;
        
        if (suited) {
          // For suited hands, use the same suit
          card1Suit = selectedHand.card1[1] || 'h';
          card2Suit = card1Suit;
        } else {
          // For offsuit hands, ensure different suits
          card1Suit = 'h';
          card2Suit = 'd';
        }
        
        const combo = {
          card1: firstCard + card1Suit,
          card2: value + card2Suit,
          frequency: 0,
          isExactHand: value === secondCard
        };
        
        const comboInRange = range.find(h => 
          h.card1 && h.card2 &&
          h.card1[0] === firstCard && 
          h.card2[0] === value && 
          (h.card1[1] === h.card2[1]) === suited
        );
        
        return {
          ...combo,
          inRange: !!comboInRange,
          frequency: comboInRange?.frequency || 0
        };
      });

    const targetFrequency = currentHandInRange?.frequency || 0;

    // Filter hands that match our exact frequency
    let matchingHands = possibleCombos.filter(hand => 
      hand.frequency === targetFrequency
    );

    // Sort by second card value
    matchingHands.sort((a, b) => {
      const aIdx = CARD_VALUES.indexOf(a.card2[0]);
      const bIdx = CARD_VALUES.indexOf(b.card2[0]);
      return bIdx - aIdx;
    });

    return matchingHands || [];
  }

  // Handle 50% frequency hands
  if (currentHandInRange?.frequency === 50) {
    if (suited) {
      // For suited hands with 50% frequency
      const suitToUse = selectedHand.card1[1] || 'h';
      return [{
        card1: firstCard + suitToUse,
        card2: secondCard + suitToUse,
        frequency: 50,
        isExactHand: true
      }];
    } else {
      // For offsuit hands with 50% frequency - ensure different suits
      // Use 'd' for first card and 'h' for second card as in original code
      const suit1 = 'd';
      const suit2 = 'h';
      
      return [{
        card1: firstCard + suit1,
        card2: secondCard + suit2,
        frequency: 50,
        isExactHand: true
      }];
    }
  }

  // Handle other hands
  if (currentHandInRange) {
    if (suited) {
      // For suited hands, use the same suit
      const suitToUse = selectedHand.card1[1] || 'h';
      return [{
        card1: firstCard + suitToUse,
        card2: secondCard + suitToUse,
        frequency: currentHandInRange.frequency,
        isExactHand: true
      }];
    } else {
      // For offsuit hands, ensure different suits
      const suit1 = selectedHand.card1[1] || 'h';
      let suit2 = selectedHand.card2[1] || 'd';
      
      // Make sure suits are different
      if (suit1 === suit2) {
        suit2 = suit1 === 'h' ? 'd' : 'h';
      }
      
      return [{
        card1: firstCard + suit1,
        card2: secondCard + suit2,
        frequency: currentHandInRange.frequency,
        isExactHand: true
      }];
    }
  }

  return [];
};

const RangeContainer = ({ selectedHand, range = [], onHandsResolved }) => {
  const hands = useMemo(() => {
    console.log('RangeContainer render:', {
      hasSelectedHand: !!selectedHand,
      hasRange: range && range.length > 0
    });
    
    if (!selectedHand || !range || !range.length) {
      console.log('No selected hand or empty range');
      return [];
    }
    
    try {
      return analyzeRange(selectedHand, range);
    } catch (error) {
      console.error('Error analyzing range:', error);
      return [];
    }
  }, [selectedHand, range]);

  // Report the resolved hand count to the parent (e.g. so an ancestor screen
  // can compute row count / decide on scroll behavior) without duplicating
  // the analyzeRange logic there.
  useEffect(() => {
    if (typeof onHandsResolved === 'function') {
      onHandsResolved(hands?.length || 0);
    }
  }, [hands, onHandsResolved]);

  if (!hands || !hands.length) {
    console.log('No similar hands to display');
    return <div className="range-message">No similar hands to display</div>;
  }

  return (
    <div className="range-container">
      <div className="range-content">
        <div className="range-column">
          {hands.map((hand, index) => (
            <HandStack 
              key={`hand-${index}`}
              firstCard={hand.card1[0]}
              secondCard={hand.card2[0]}
              exactHand={selectedHand}
              index={index}
              frequency={hand.frequency}
              isExactHand={hand.isExactHand}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RangeContainer;